import { useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  finishIntentionalLogout,
  isIntentionalLogoutPending,
  useUserStore,
} from '@/stores/userStore';
import { isWhiteListed } from './config';
import { toast } from 'sonner';
import { cancelManager } from '@/api';
import { routes } from './routes';
import { matchRouteConfig } from './utils';
import { getPermListOuter, type PermissionNode } from '@/api/modules/user/sysPermission';
import { APP_CONFIG } from '@/config/env';
import { getHasPermissionRoutes } from './getHasPermissionRoutes';
import axios from 'axios';

interface AuthGuardProps {
  children: React.ReactNode;
}

/**
 * 路由守卫组件
 * 负责路由跳转前的权限校验
 */
export function AuthGuard({ children }: AuthGuardProps) {
  const location = useLocation();
  const navigate = useNavigate();
  const { token, userInfo } = useUserStore();
  const lastNotLoggedInPathRef = useRef<string | null>(null);

  useEffect(() => {
    const refreshButtonPermissions = async () => {
      if (token) {
        const res = await getPermListOuter();
        if (APP_CONFIG.isDev && res.data) {
          function traversePermissions(
            nodes: PermissionNode[],
            currentPath: string,
            result: Record<string, string>
          ) {
            for (const node of nodes) {
              // 构建当前节点的完整路径
              const path = currentPath ? `${currentPath}/${node.permName}` : node.permName;

              // 如果当前节点是按钮权限 (permType === 3)，将其添加到结果对象中
              if (node.permType === 3) {
                result[path] = node.permCode;
              }

              // 递归处理子节点
              if (node.children && node.children.length > 0) {
                traversePermissions(node.children, path, result);
              }
            }
          }
          const map = {};
          traversePermissions(res.data, '', map); // 从根开始，初始路径为空字符串
          // 自动生成权限常量文件
          fetch('/__generate_perms', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ map }),
          })
            .then((res) => {
              if (res.ok) {
                console.log('权限常量文件生成成功');
              } else {
                console.error('权限常量文件生成失败');
              }
            })
            .catch((e) => {
              console.error('权限常量文件生成请求失败', e);
            });
        }
      }
    };
    void refreshButtonPermissions().catch((error) => {
      if (!axios.isCancel(error)) console.error('刷新按钮权限失败:', error);
    });
  }, [token]);

  useEffect(() => {
    const validateRoute = async () => {
      const currentPath = location.pathname;

      if (currentPath === '/login') {
        finishIntentionalLogout();
      }

      // 使用 window.location.search 获取 hash 之前的查询参数
      const urlSearchParams = new URLSearchParams(window.location.search);
      const state = urlSearchParams.get('state');
      // 检查是否是第三方回跳（优先处理）
      if (state === 'playlet') {
        const queryString = urlSearchParams.toString();
        const targetPath = `/asset-center/playlet${queryString ? `?${queryString}` : ''}`;
        console.log(targetPath, 'targetPath');
        navigate(targetPath, { replace: true });
        return;
      }

      if (state?.startsWith('account-management')) {
        const queryString = urlSearchParams.toString();
        const targetPath = `/asset-center/account-management${queryString ? `?${queryString}` : ''}`;
        console.log(targetPath, 'targetPath');
        navigate(targetPath, { replace: true });
        return;
      }

      if (token) {
        lastNotLoggedInPathRef.current = null;
      }

      const knownRoute = matchRouteConfig(currentPath, routes);
      if (knownRoute?.meta?.requireAuth === false) {
        return;
      }

      // 白名单路径直接放行
      if (isWhiteListed(currentPath)) {
        return;
      }

      // 未登录用户重定向到登录页
      if (!token) {
        if (!isIntentionalLogoutPending() && lastNotLoggedInPathRef.current !== currentPath) {
          lastNotLoggedInPathRef.current = currentPath;
          toast.error('当前未登录，请先登录');
        }
        navigate('/login', {
          replace: true,
          state: { from: currentPath },
        });
        return;
      }

      if (userInfo?.permissionCodes?.length === 0) {
        toast.error('您没有权限访问本系统，请联系管理员');
        navigate('/login', {
          replace: true,
          state: { from: currentPath },
        });
        return;
      }

      if (!knownRoute) {
        console.warn('未知的路由路径,重定向到/404', currentPath, routes);
        navigate('/404', { replace: true });
        return;
      }

      const permissionCodes = userInfo?.permissionCodes ?? [];
      const authorizedRoutes = getHasPermissionRoutes(permissionCodes);
      const authorizedRoute = matchRouteConfig(currentPath, authorizedRoutes);

      if (!authorizedRoute) {
        toast.error('暂无权限访问');
        navigate('/404', { replace: true });
      }
    };

    validateRoute();
  }, [location.pathname, location.search, token, userInfo?.permissionCodes, navigate]);

  useEffect(() => {
    return () => {
      //非根路由重定向时(避免重定向目标页面的初始请求被取消)，取消所有请求
      if (location.pathname !== '/') {
        console.log(
          `[AuthGuard]页面切换，调用：cancelAllRequests，取消在${location.pathname}页面时发起但未完成的请求`
        );
        cancelManager.cancelAllRequests();
      }
    };
  }, [location.pathname]);

  return <>{children}</>;
}
